import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Modal, TextField, MenuItem } from '@mui/material';
import CronConfig from './CronComponent.js';
import { useDomain } from '../../context/DomainContext.js';

function SchedulerModal({ openModal, selectedSchedule, handleCloseModal, handleSave, templates }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [taskType, setTaskType] = useState('template_update');
  const [templateSelected, setTemplateSelected] = useState(null);
  const [isNewSchedule, setIsNewSchedule] = useState(false);
  const [templateError, setTemplateError] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const [cronValue, setCronValue] = useState('0 0 00 ? * MON *');

  const { selectedDomain } = useDomain();

  useEffect(() => {
    if (selectedSchedule?.name) {
      setName(selectedSchedule.name.match(/jobs\/([^/]+)$/)?.[1] || "");
      setDescription(selectedSchedule.description.toLowerCase());
      setCronValue(selectedSchedule.schedule || '0 0 00 ? * MON *');
      
      const templateId = selectedSchedule.httpTarget?.body?.signature_template_id || null;
      if (templateId === 'google-workspace-sync') {
        setTaskType('workspace_sync');
        setTemplateSelected(null);
      } else {
        setTaskType('template_update');
        setTemplateSelected(templateId);
      }
      setIsNewSchedule(false);
    } else {
      setName('');
      setDescription('');
      setTaskType('template_update');
      setTemplateSelected(null);
      setCronValue('0 0 00 ? * MON *');
      setIsNewSchedule(true);
    }
  }, [selectedSchedule, openModal]);

  const handleButtonSave = () => {
    if (!name) { setNameError(true); return; }
    if (!description) { setDescriptionError(true); return; }

    let templateIdToSend = templateSelected;
    if (taskType === 'workspace_sync') {
      templateIdToSend = 'google-workspace-sync';
    } else {
      if (!templateSelected) { setTemplateError(true); return; }
    }

    const scheduleData = {
      name,
      description,
      signature_template_id: templateIdToSend,
      domain_id: selectedDomain,
      schedule: cronValue,
    };
    handleSave(scheduleData, isNewSchedule);
  };

  const validateName = (value) => {
    const validRegex = /^[a-zA-Z\d_-]{1,500}$/;
    return validRegex.test(value);
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    setNameError(!validateName(value));
  };

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
    setDescriptionError(false);
  };

  return (
    <Modal open={openModal} onClose={handleCloseModal}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Typography variant="h6" gutterBottom>
          {selectedSchedule?.name ? 'Editar tarea' : 'Crear nueva tarea'}
        </Typography>
        <TextField
          label="Nombre"
          name="name"
          value={name}
          onChange={handleNameChange}
          fullWidth
          sx={{ marginBottom: '8px', marginTop: '16px' }}
          disabled={!isNewSchedule}
          error={nameError}
          helperText={nameError ? "El nombre debe contener solo letras, números, guiones o guiones bajos (máx. 500 caracteres)." : ""}
        />
        <TextField
          label="Descripción"
          name="description"
          value={description}
          onChange={handleDescriptionChange}
          fullWidth
          multiline
          rows={3}
          disabled={!isNewSchedule}
          sx={{ marginBottom: '8px', marginTop: '16px' }}
          error={descriptionError}
          helperText={descriptionError ? "Descripción es requerido" : ""}
        />

        {/* Task Type Selector */}
        <TextField
          select
          fullWidth
          value={taskType}
          onChange={(event) => {
            setTaskType(event.target.value);
            setTemplateError(false);
          }}
          label="Tipo de Tarea"
          sx={{ marginBottom: '8px', marginTop: '16px' }}
          disabled={!isNewSchedule}
        >
          <MenuItem value="template_update">Actualización de Firmas (Plantilla)</MenuItem>
          <MenuItem value="workspace_sync">Sincronización y Validación Google Workspace</MenuItem>
        </TextField>

        {/* Template Selector (Only shown for template updates) */}
        {taskType === 'template_update' && (
          <TextField
            key={'selectTemplate'}
            select
            fullWidth
            value={templateSelected}
            onChange={(event) => setTemplateSelected(event.target.value)}
            label="Template"
            error={templateError}
            helperText={templateError ? "Template es requerido" : ""}
            sx={{ marginBottom: '8px', marginTop: '16px' }}
            InputLabelProps={{ shrink: !!templateSelected }}
            disabled={!isNewSchedule}
          >
            {Array.isArray(templates) && templates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.template_name}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Typography variant="body1" gutterBottom style={{ margin: 0, marginBottom: 8, marginTop: 16 }}>
          Configuración de Repetición
        </Typography>
        <CronConfig initialCron={cronValue} onCronChange={setCronValue} />
        <Box sx={{ textAlign: 'right', marginTop: '16px' }}>
          <Button onClick={handleCloseModal} sx={{ marginRight: 1 }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleButtonSave}>
            Guardar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default SchedulerModal;
